import React, { useState, useRef, useMemo, useEffect } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { Spinner, Form } from 'components/base';
import {
  getNumberOfAddablePages,
  getNumberOfGenerationsFromPage,
  getTemplatePage,
  getTreeTypeFromPage,
} from 'editor-utils/template';
import TOC from 'editor-utils/toc';
import queryString from 'utils/queryString';
import useModalContext from 'components/context/modal';
import useUserContext from 'ecommerce/context/user';
import produce from 'utils/produce';

import { Layout, Modal } from '../components/rich';
import { Button, Logo } from '../components/base';
import Icon, { ICON_TYPE } from '../components/base/icon';
import useEditorItemContext from '../context/editorItemContext';
import useEditorContext from '../context/editorContext';
import AncestryWebSocketSynchronizer from '../new/components/ancestryWebSocketSynchronizer';
import generateAncestryData from '../generator/generateAncestryData';
import useHashQuery from 'ecommerce/hooks/useHash';

const PolotnoLoading = () => {
  const [error, setError] = useState(false);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setError(true);
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="mx-auto text-center">
      <Spinner theme="white" className="mb-3" />
      {error && (
        <div className="text-white">
          Oops, something went wrong.
          <br />
          Please refresh the page to ensure you&apos;re using the latest version.
        </div>
      )}
    </div>
  );
};

const Polotno = dynamic(() => import('../polotno'), {
  ssr: false,
  loading: () => <PolotnoLoading />,
});

const Select = styled(Form.Select)`
  padding: 5px 1.5rem 5px 5px !important;
  background-position: calc(100% - 0.5rem) center !important;
  width: auto;
  color: #ffffff;
  height: auto;
  border: 1px solid #ffffff55;
  background: #ffffff11;
  margin: 0 8px;
  border-radius: 5px;

  &:focus {
    color: #ffffff !important;
    background-color: #ffffff11 !important;
  }
`;

const SelectOption = styled.option`
  color: #000 !important;
  background-color: #fff;
`;

const PageSelect = ({ value, onChange, pages }) => {
  const handleOnChange = e => {
    onChange(parseInt(e.target.value, 10));
  };

  return (
    <Select value={value} onChange={handleOnChange}>
      {pages.map(page => (
        <SelectOption key={page.pageNumber} value={page.pageNumber}>
          {page.pageLabel}
        </SelectOption>
      ))}
    </Select>
  );
};

const withPolotno = Component => {
  const WrappedComponent = props => {
    const [data, setData] = useState();

    const [polotnoHash, setPolotnoHash] = useHashQuery("polotno")

    const dataRef = useRef(data);
    useEffect(() => {
      dataRef.current = data;
    }, [data]);

    const polotnoRef = useRef();
    const onChangeCallbackRef = useRef();
    const onChangePageCallbackRef = useRef();

    const editorContext = useEditorContext();
    const userContext = useUserContext();
    const modalContext = useModalContext();
    const { item, template, numberOfPages, config } = useEditorItemContext();

    const start = (args, onChangeCallback = () => { }, onChangePageCallback = () => { }) => {
      setPolotnoHash(null)
      setData({
        pageKey: args.pageKey,
        onAddPage: args.onAddPage,
      });

      onChangeCallbackRef.current = onChangeCallback;
      onChangePageCallbackRef.current = onChangePageCallback;
    };

    const save = polotnoData => {
      if (!polotnoRef.current) {
        return;
      }

      const result = polotnoData ?? polotnoRef.current.getData({ photos: item.photos });
      onChangeCallbackRef.current({
        pageKey: dataRef.current.pageKey,
        page: result.pages[0],
      });
    };

    const close = () => {
      setPolotnoHash(undefined)
      setData();
    };

    useEffect(() => {
      if (polotnoHash === undefined) {
        close()
      } else if (!data) {
        close()
      }
    }, [polotnoHash, data])

    const handleOnDoneClick = () => {
      save();
      close();
    };

    const renderPolotno = page => {
      const pageIndex = page.index;
      const isCover = template.cover && pageIndex === 0;
      const templatePage = getTemplatePage(template, page.id, isCover ? 'cover' : 'pages');

      const changePage = (newPageIndex, newPageData = null) => {
        if (!polotnoRef.current) {
          return;
        }

        const newPage = newPageData ?? item.pages[newPageIndex];

        polotnoRef.current.setData(
          [newPage],
          item.photos,
          template,
          template.cover && newPageIndex === 0 ? 'cover' : 'pages',
        );

        setData(value => ({
          ...value,
          pageKey: newPage.key,
        }));

        onChangePageCallbackRef.current(newPage);
      };

      const handlePreviousPageClick = () => {
        if (!polotnoRef.current) {
          return;
        }

        save();

        const minPageIndex = template.cover ? 1 : 0;

        const newPageIndex = Math.max(minPageIndex, pageIndex - 1);
        if (pageIndex !== newPageIndex) {
          changePage(newPageIndex);
        }
      };

      const handleNextPageClick = () => {
        if (!polotnoRef.current) {
          return;
        }

        save();

        const newPageIndex = Math.min(pageIndex + 1, item.pages.length - 1);
        if (pageIndex !== newPageIndex) {
          changePage(newPageIndex);
        }
      };

      const handleRegeneratePageClick = () => {
        modalContext.confirm(
          <>
            Regenerating this page will fetch the latest information from Ancestry.com and recreate
            this page.
            <br />
            <br />
            <strong>Warning:</strong> You will lose any manual edits to the information and any
            design changes you have made to this page.
          </>,
          {
            title: 'Are you sure?',
            onConfirm: async () => {
              const { treeId, treeName } = item.additionalInfo;
              const persons = page.tags;
              const numberOfGenerations = getNumberOfGenerationsFromPage(templatePage) ?? 0;
              const treeType = getTreeTypeFromPage(templatePage);
              const optionIds = [templatePage.optionId];

              modalContext.show(
                <AncestryWebSocketSynchronizer
                  ancestryAuth={userContext.ancestryAuth}
                  accessToken={userContext.accessToken}
                  productId={template.id}
                  persons={persons}
                  numberOfGenerations={numberOfGenerations}
                  treeName={treeName}
                  treeType={treeType}
                  progressLabel="We’re turning your data from Ancestry.com into a page preview."
                  onSuccess={async ({ onProgress, close: popupClose }) => {
                    const { data: newData, error } = await generateAncestryData(
                      userContext.accessToken,
                      template,
                      userContext.ancestryAuth,
                      {
                        type: "ancestry",
                        arrayOfPersonIds: [persons],
                        optionIds,
                        numberOfGenerations,
                        withoutCover: true,
                        treeId,
                        treeName,
                        treeType,
                      },
                      item.photos,
                      ({ pending, total }) => {
                        onProgress(((total - pending) / total) * 100);
                      },
                    );

                    if (error) {
                      popupClose();
                      modalContext.confirm(<>{error}</>, {
                        title: 'Error while rebuilding your page',
                        closeable: false,
                        confirmLabel: 'OK',
                      });
                      return;
                    }

                    const pages = newData.pages.filter(newPage => {
                      // it can be only same page id
                      return newPage.id === templatePage.id;
                    });

                    if (pages.length === 1) {
                      const newPage = produce(pages[0], draft => {
                        draft.key = page.key;
                        if (page.pageNumber != null) {
                          draft.placeholders = pages[0].placeholders.map(p => {
                            if (p.id === 'page-num') {
                              return {
                                ...p,
                                data: {
                                  value: `${page.pageNumber}`,
                                },
                              };
                            }
                            return p;
                          });
                        }
                      });
                      editorContext.setPhotos(item.id, newData.photos);
                      polotnoRef.current.setData([newPage], newData.photos, template, 'pages');
                    }

                    popupClose();
                  }}
                />,
                {
                  title: 'Rebuilding your page',
                  closeable: false,
                },
              );

              try {
                gtag('event', 'regenerate_page', {
                  event_category: 'editor',
                });
              } catch (e) { }
            },
          },
        );
      };

      const handleNewPageClick = () => {
        if (!polotnoRef.current) {
          return;
        }

        save();

        const newPageIndex = pageIndex + 1;
        data.onAddPage(newPageIndex, newPage => {
          changePage(newPageIndex, newPage);
        });
      };

      const handleOnPageChange = pageNumber => {
        if (!polotnoRef.current) {
          return;
        }

        save();

        const newPageIndex = template.cover ? pageNumber : pageNumber - 1;
        if (pageIndex !== newPageIndex) {
          changePage(newPageIndex);
        }
      };

      const label = isCover ? (
        <>Cover Page</>
      ) : (
        <div className="d-flex align-items-center">
          <span>Page</span>
          <PageSelect
            value={template.cover ? pageIndex : pageIndex + 1}
            onChange={handleOnPageChange}
            pages={item.pages}
          />
          <span>of {numberOfPages}</span>
        </div>
      );

      const isNewPageAvailable =
        getNumberOfAddablePages(template, item.pages.length) > 0 &&
        data.onAddPage &&
        !isCover &&
        !TOC.isPage(page);

      const isRegenerateAvailable =
        page.id != null && (page.tags ?? []).length > 0 && templatePage?.optionId != null;

      return (
        <>
          <Modal visible size="fullScreen" backgroundColor="primary">
            <Layout.Container style={{ justifyContent: 'space-between' }}>
              <div className="d-flex">
                <Button large p={0} onClick={handleOnDoneClick}>
                  <Logo white />
                </Button>
                <Modal.Paging>
                  {!isCover && (
                    <Button
                      circular
                      variant="tertiary"
                      onClick={handlePreviousPageClick}
                      backgroundColor={null}
                    >
                      <Icon width={18} height={18} type={ICON_TYPE.ARROW2} />
                    </Button>
                  )}
                  <Modal.Heading>{label}</Modal.Heading>
                  {!isCover && (
                    <Button
                      circular
                      variant="tertiary"
                      onClick={handleNextPageClick}
                      backgroundColor={null}
                    >
                      <Icon width={18} height={18} type={ICON_TYPE.ARROW2} rotate={180} />
                    </Button>
                  )}
                </Modal.Paging>
              </div>
              <div>
                {isRegenerateAvailable && (
                  <Button variant="tertiary" large onClick={handleRegeneratePageClick} mr={1}>
                    <Icon mr={1} width={18} height={18} type={ICON_TYPE.REFRESH} />
                    Regenerate Page
                  </Button>
                )}
                {isNewPageAvailable && (
                  <Button variant="tertiary" large onClick={handleNewPageClick} mr={1}>
                    <Icon mr={1} width={18} height={18} type={ICON_TYPE.PAGE} />
                    Add New Blank Page
                  </Button>
                )}
                <Button active variant="tertiary" large onClick={handleOnDoneClick}>
                  Back to Preview
                </Button>
              </div>
            </Layout.Container>
            <Layout.Main style={{ padding: '1rem 0 0 0' }}>
              <Polotno
                innerRef={polotnoRef}
                config={config}
                pages={[page]}
                photos={item.photos}
                additionalInfo={item.additionalInfo}
                template={template}
                type={isCover ? 'cover' : 'pages'}
                spinnerTheme="white"
                onChange={save}
              />
            </Layout.Main>
          </Modal>
        </>
      );
    };

    const page = useMemo(() => {
      if (data?.pageKey) {
        return item.pages.find(p => p.key === data.pageKey);
      }
      return null;
    }, [data, item.pages]);

    return (
      <>
        <Component {...props} startPolotno={start} closePolotno={close} isPolotnoOpen={!!page} />
        {page && renderPolotno(page)}
      </>
    );
  };

  WrappedComponent.getInitialProps = async ctx => {
    if (Component.getInitialProps) {
      return Component.getInitialProps(ctx);
    }

    return {};
  };

  return WrappedComponent;
};

export default withPolotno;
